import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ICity } from "../../interfaces";
import useAuth from "../../hooks/useAuth";
import { EmitSuccessAlert } from "../../utils/EmitSuccessAlert";
import { EmitError } from "../../utils/EmitError";
import { removeAcento } from "../../utils/RemoveAcentos";

export default function useSelectCityModal() {
    const [cities, setCities] = useState<Array<ICity>>([]);
    const [selectedCityId, setSelectedCityId] = useState<number | undefined>(undefined);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);

    const {
        user,
        activeCity,
        setUserActiveCity,
        isSelectCityModalOpen,
        handleCloseSelectCityModal,
        signOut,
    } = useAuth();

    const navigate = useNavigate();

    // Populate user's available cities
    useEffect(() => {
        if (!user?.cities) {
            return;
        }

        const sorted = [...user.cities].sort((a, b) =>
            `${a.city} - ${a.uf}`.localeCompare(`${b.city} - ${b.uf}`)
        );
        setCities(sorted);

        if (activeCity?.id) {
            setSelectedCityId(activeCity.id);
        } else if (sorted.length > 0) {
            setSelectedCityId(sorted[0].id);
        }
    }, [user, activeCity?.id]);

    // Filter cities by search term (accent-insensitive)
    const filteredCities = useMemo(() => {
        if (!searchQuery.trim()) return cities;
        const query = removeAcento(searchQuery.toLowerCase().trim());
        return cities.filter((c) => {
            const cityName = removeAcento((c.city || c.name || "").toLowerCase());
            const uf = (c.uf || "").toLowerCase();
            const fullStr = `${cityName} ${uf}`;
            return cityName.includes(query) || uf.includes(query) || fullStr.includes(query);
        });
    }, [cities, searchQuery]);

    const handleSelectCity = useCallback(
        async (cityToSelect?: ICity) => {
            const targetCity = cityToSelect || cities.find((c) => c.id === selectedCityId);

            if (!targetCity) {
                return;
            }

            try {
                setIsSubmiting(true);

                await setUserActiveCity(targetCity);

                localStorage.removeItem("@NotaMunicipio:appUserFilters");
                localStorage.removeItem("@NotaMunicipio:invoicesCompanyByCity");
                localStorage.removeItem("@NotaMunicipio:allInvoices");

                setIsSubmiting(false);

                navigate("/dashboard");
                EmitSuccessAlert(`Município ${targetCity.city} - ${targetCity.uf} selecionado com sucesso!`);
                handleCloseSelectCityModal();
            } catch (err) {
                EmitError(err, true, "selectCityModal_selectCity");
                setIsSubmiting(false);
            }
        },
        [cities, selectedCityId, setUserActiveCity, navigate, handleCloseSelectCityModal]
    );

    const handleLogout = useCallback(() => {
        handleCloseSelectCityModal();
        signOut();
        navigate("/");
    }, [handleCloseSelectCityModal, signOut, navigate]);

    return {
        cities,
        filteredCities,
        searchQuery,
        setSearchQuery,
        selectedCityId,
        setSelectedCityId,
        isSubmiting,
        handleSelectCity,
        activeCity,
        isSelectCityModalOpen,
        handleCloseSelectCityModal,
        handleLogout,
    };
}
